import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ticketAPI, metadataAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Upload, X, Paperclip } from 'lucide-react';

// ── Issue-type → field rules matrix (from spec) ──
// Values: 'required' | 'optional' | 'hide' | 'Low' | 'Medium' (auto-set)
const FIELD_RULES = {
  'Fix':                      { feature: 'required', subfeature: 'required', module: 'required', mart: 'required', expected: 'required', observed: 'required', expectedLabel: 'Expected behaviour',                    observedLabel: 'Observed behaviour',           impact: 'required', severity: 'required' },
  'Incident':                 { feature: 'hide',     subfeature: 'hide',     module: 'hide',     mart: 'hide',     expected: 'optional', observed: 'required', expectedLabel: 'Recovery actions attempted',                   observedLabel: 'Incident description',   impact: 'required', severity: 'required' },
  'Assistance':               { feature: 'required', subfeature: 'required', module: 'required', mart: 'required', expected: 'optional', observed: 'required', expectedLabel: 'What all have you tried so far?',              observedLabel: 'What help do you require?', impact: 'Low',   severity: 'Low' },
  'Performance':              { feature: 'required', subfeature: 'required', module: 'required', mart: 'required', expected: 'optional', observed: 'required', expectedLabel: 'Did you retry it later?',    observedLabel: 'Describe the action you were taking',      impact: 'required', severity: 'required' },
  'Data mismatch':            { feature: 'hide',     subfeature: 'hide',     module: 'required', mart: 'required', expected: 'hide', observed: 'required',     expectedLabel: '',               observedLabel: 'Describe the discrepancy',                             impact: 'required', severity: 'required' },
  'Data reload':              { feature: 'hide',     subfeature: 'hide',     module: 'required', mart: 'required', expected: 'hide', observed: 'required',     expectedLabel: '', observedLabel: 'List the months you require reload for',                             impact: 'Medium',   severity: 'Medium' },
  'Vulnerability management': { feature: 'hide',     subfeature: 'hide',     module: 'hide',     mart: 'hide',     expected: 'hide', observed: 'required',     expectedLabel: '', observedLabel: 'List the artefacts and the vulnerabilities',                       impact: 'required', severity: 'required' },
  'Patch updates':            { feature: 'hide',     subfeature: 'hide',     module: 'hide',     mart: 'hide',     expected: 'hide', observed: 'required',     expectedLabel: '',               observedLabel: 'Describe the requirement',                             impact: 'required', severity: 'required' },
};
const DEFAULT_RULES = { feature: 'required', subfeature: 'optional', module: 'required', mart: 'optional', expected: 'optional', observed: 'required', expectedLabel: 'Expected behaviour', observedLabel: 'Observed behaviour', impact: 'required', severity: 'required' };

export default function TicketCreatePage() {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm();
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();

  const [issueTypes, setIssueTypes] = useState([]);
  const [impactLevels, setImpactLevels] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [features, setFeatures] = useState([]);
  const [modules, setModules] = useState([]);
  const [marts, setMarts] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(user?.client_id || '');

  // Sub-feature state (driven by selected parent feature)
  const [subFeatures, setSubFeatures] = useState([]);

  // File upload state
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  // ── Load metadata INDEPENDENTLY (not Promise.all) ──
  // This prevents one failing endpoint from blanking out all dropdowns.
  useEffect(() => {
    metadataAPI.getIssueTypes()
      .then(r => setIssueTypes(r.data))
      .catch(err => console.error('Failed to load issue types:', err));

    metadataAPI.getImpactLevels()
      .then(r => setImpactLevels(r.data))
      .catch(err => console.error('Failed to load impact levels:', err));

    metadataAPI.getSeverityLevels()
      .then(r => setSeverityLevels(r.data))
      .catch(err => console.error('Failed to load severity levels:', err));

    metadataAPI.getFeatures()
      .then(r => setFeatures(r.data))
      .catch(err => console.error('Failed to load features:', err));

    if (isStaff) {
      metadataAPI.getClients()
        .then(r => setClients(r.data))
        .catch(() => {});
    }
  }, [isStaff]);

  // Load client-specific data when client changes
  useEffect(() => {
    const cid = selectedClientId || user?.client_id;
    if (cid) {
      metadataAPI.getClientModules(cid).then(r => setModules(r.data)).catch(() => setModules([]));
      metadataAPI.getClientMarts(cid).then(r => setMarts(r.data)).catch(() => setMarts([]));
      if (isStaff) {
        metadataAPI.getClientUsers(cid).then(r => setClientUsers(r.data)).catch(() => setClientUsers([]));
      }
    }
  }, [selectedClientId, user?.client_id, isStaff]);

  // Watch selections for hints
  const selectedIssueTypeId = watch('issue_type_id');
  const selectedIssueTypeName = issueTypes.find(t => String(t.id) === String(selectedIssueTypeId))?.name;
  const fr = FIELD_RULES[selectedIssueTypeName] || DEFAULT_RULES;
  const selectedImpact = watch('impact_id');
  const selectedSeverity = watch('severity_id');
  const selectedFeature = watch('feature');

  const issueTypeDef = issueTypes.find(t => String(t.id) === String(selectedIssueTypeId))?.description;
  const impactDef = impactLevels.find(i => String(i.id) === String(selectedImpact))?.definition;
  const severityDef = severityLevels.find(s => String(s.id) === String(selectedSeverity))?.definition;

  // Update sub-features when parent feature changes
  useEffect(() => {
    if (selectedFeature && selectedFeature !== '__custom') {
      const parent = features.find(f => f.name === selectedFeature);
      setSubFeatures(parent?.children || []);
    } else {
      setSubFeatures([]);
    }
  }, [selectedFeature, features]);

  // Auto-set or clear impact/severity when issue type changes
  useEffect(() => {
    if (fr.impact === 'required') {
      // User must choose — clear any preset from previous issue type
      setValue('impact_id', '');
    } else {
      const match = impactLevels.find(i => i.name === fr.impact);
      if (match) setValue('impact_id', String(match.id));
    }
    if (fr.severity === 'required') {
      setValue('severity_id', '');
    } else {
      const match = severityLevels.find(s => s.name === fr.severity);
      if (match) setValue('severity_id', String(match.id));
    }
    // Also clear hidden fields to avoid stale data
    if (fr.feature === 'hide') { setValue('feature', ''); setValue('sub_feature', ''); }
    if (fr.module === 'hide') setValue('module', '');
    if (fr.mart === 'hide') setValue('mart', '');
    if (fr.observed === 'hide') setValue('observed', '');
    if (fr.expected === 'hide') setValue('expected', '');
  }, [fr, impactLevels, severityLevels, setValue]);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB limit`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        issue_type_id: parseInt(data.issue_type_id),
        feature: fr.feature !== 'hide'
          ? (data.feature === '__custom' ? 'Other' : data.feature || null)
          : null,
        sub_feature: (fr.subfeature !== 'hide' && data.sub_feature) ? data.sub_feature : null,
        module: fr.module !== 'hide' ? (data.module || null) : null,
        mart: (fr.mart !== 'hide' && data.mart) ? data.mart : null,
        description: {
          observed: fr.observed !== 'hide' ? (data.observed || null) : null,
          expected: fr.expected !== 'hide' ? (data.expected || null) : null,
        },
        impact_id: parseInt(data.impact_id),
        severity_id: parseInt(data.severity_id),
      };
      if (isStaff && data.reporter_user_id) {
        payload.reporter_user_id = parseInt(data.reporter_user_id);
      }

      const resp = await ticketAPI.create(payload);
      const ticketId = resp.data.id;
      const issueId = resp.data.issue_id;

      if (files.length > 0) {
        let uploaded = 0;
        for (const file of files) {
          try { await ticketAPI.uploadAttachment(ticketId, file, 'CREATION'); uploaded++; }
          catch (err) { toast.error(`Failed to upload ${file.name}`); }
        }
        if (uploaded > 0) toast.success(`${uploaded} file(s) uploaded`);
      }

      toast.success(`Ticket ${issueId} created successfully`);
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Raise a ticket</h1>
        <p>Submit a new support ticket for the Winnow platform</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card" style={{ maxWidth: 720 }}>
          {/* Staff: Select client and user */}
          {isStaff && (
            <>
              <div className="form-group">
                <label className="form-label">Client <span className="required">*</span></label>
                <select className="form-select" value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)} required>
                  <option value="">Select client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Client user <span className="required">*</span></label>
                <select className="form-select" {...register('reporter_user_id', { required: true })}>
                  <option value="">Select user...</option>
                  {clientUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
                {errors.reporter_user_id && <p className="form-error">Client user is required</p>}
              </div>
            </>
          )}

          {/* Client info (read-only for clients) */}
          {!isStaff && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">Name</label>
                <input className="form-input" value={user?.name || ''} disabled />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">Email</label>
                <input className="form-input" value={user?.email || ''} disabled />
              </div>
            </div>
          )}

          {/* Issue Type — with description hint */}
          <div className="form-group">
            <label className="form-label">Issue type <span className="required">*</span></label>
            <select className="form-select" {...register('issue_type_id', { required: true })}>
              <option value="">Select type...</option>
              {issueTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {issueTypeDef && <p className="form-hint" style={{ marginTop: 4, fontStyle: 'italic' }}>{issueTypeDef}</p>}
            {errors.issue_type_id && <p className="form-error">Issue type is required</p>}
          </div>

          {/* Feature / Sub-feature — conditionally shown */}
          {fr.feature !== 'hide' && (
          <div style={{ display: 'flex', gap: 16 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Feature {fr.feature === 'required' && <span className="required">*</span>}</label>
              <select className="form-select" {...register('feature', { required: fr.feature === 'required' })}>
                <option value="">Select feature...</option>
                {features.map(f => (
                  <option key={f.id} value={f.name}>{f.name}</option>
                ))}
                <option value="__custom">Other (custom entry)</option>
              </select>
              {errors.feature && <p className="form-error">Feature is required</p>}
            </div>
            {fr.subfeature !== 'hide' && (
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sub-feature {fr.subfeature === 'required' && <span className="required">*</span>}</label>
              {subFeatures.length > 0 ? (
                <select className="form-select" {...register('sub_feature', { required: fr.subfeature === 'required' })}>
                  <option value="">Select sub-feature...</option>
                  {subFeatures.map(sf => (
                    <option key={sf.id} value={sf.name}>{sf.name}</option>
                  ))}
                </select>
              ) : (
                <input className="form-input" {...register('sub_feature', { required: fr.subfeature === 'required' })}
                  placeholder={selectedFeature === '__custom' ? 'Enter custom feature/sub-feature' : 'Optional sub-feature'} />
              )}
            </div>
            )}
          </div>
          )}

          {/* Module / Mart — conditionally shown */}
          {(fr.module !== 'hide' || fr.mart !== 'hide') && (
          <div style={{ display: 'flex', gap: 16 }}>
            {fr.module !== 'hide' && (
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Module {fr.module === 'required' && <span className="required">*</span>}</label>
              <select className="form-select" {...register('module', { required: fr.module === 'required' })}>
                <option value="">Select module...</option>
                {modules.map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
              {modules.length === 0 && (
                <p className="form-hint" style={{ color: 'var(--color-warning, #d97706)' }}>
                  No modules found. Admin: run Hub Sync or seed client data.
                </p>
              )}
              {errors.module && <p className="form-error">Module is required</p>}
            </div>
            )}
            {fr.mart !== 'hide' && (
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Mart {fr.mart === 'required' && <span className="required">*</span>}</label>
              <select className="form-select" {...register('mart', { required: fr.mart === 'required' })}>
                <option value="">{fr.mart === 'required' ? 'Select mart...' : 'Select mart (optional)...'}</option>
                {marts.map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
              {errors.mart && <p className="form-error">Mart is required</p>}
            </div>
            )}
          </div>
          )}

          {/* Description fields — labels and visibility driven by issue type */}
          {fr.observed !== 'hide' && (
          <div className="form-group">
            <label className="form-label">{fr.observedLabel || 'Observed behaviour'} {fr.observed === 'required' && <span className="required">*</span>}</label>
            <textarea className="form-textarea" {...register('observed', { required: fr.observed === 'required' })}
              placeholder={fr.observedLabel || 'Describe what actually happened...'} rows={3} spellCheck="true" />
            {errors.observed && <p className="form-error">This field is required</p>}
          </div>
          )}
          {fr.expected !== 'hide' && (
          <div className="form-group">
            <label className="form-label">{fr.expectedLabel || 'Expected behaviour'} {fr.expected === 'required' && <span className="required">*</span>}</label>
            <textarea
                className="form-textarea"
                {...register('expected', { required: fr.expected === 'required' })}
                placeholder={fr.expectedLabel || 'Describe what you expected to happen...'}
                rows={3}
                spellCheck="true"
            />
            {errors.expected && <p className="form-error">This field is required</p>}
          </div>
          )}

          {/* Impact / Severity — auto-set or user-selected based on issue type */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Impact <span className="required">*</span></label>
              <select className="form-select" {...register('impact_id', { required: true })}
                disabled={fr.impact !== 'required'}
                style={fr.impact !== 'required' ? { background: 'var(--color-bg)', opacity: 0.8 } : {}}>
                <option value="">Select impact...</option>
                {impactLevels.map(i => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
              {fr.impact === 'required' && impactDef && <p className="form-hint">{impactDef}</p>}
              {fr.impact !== 'required' && (() => {
                const def = impactLevels.find(i => i.name === fr.impact)?.definition;
                return def ? <p className="form-hint">Auto-set to {fr.impact}. {def}</p> : null;
              })()}
              {errors.impact_id && <p className="form-error">Impact is required</p>}
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Severity <span className="required">*</span></label>
              <select className="form-select" {...register('severity_id', { required: true })}
                disabled={fr.severity !== 'required'}
                style={fr.severity !== 'required' ? { background: 'var(--color-bg)', opacity: 0.8 } : {}}>
                <option value="">Select severity...</option>
                {severityLevels.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {fr.severity === 'required' && severityDef && <p className="form-hint">{severityDef}</p>}
              {fr.severity !== 'required' && (() => {
                const def = severityLevels.find(s => s.name === fr.severity)?.definition;
                return def ? <p className="form-hint">Auto-set to {fr.severity}. {def}</p> : null;
              })()}
              {errors.severity_id && <p className="form-error">Severity is required</p>}
            </div>
          </div>

          {/* File upload */}
          <div className="form-group">
            <label className="form-label">Supporting screenshots</label>
            <div
              style={{
                border: '2px dashed var(--color-border)', borderRadius: 8,
                padding: 16, textAlign: 'center', cursor: 'pointer',
                background: 'var(--color-bg-subtle, #fafafa)', transition: 'border-color 0.2s',
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
              onDrop={(e) => {
                e.preventDefault(); e.currentTarget.style.borderColor = '';
                const dropped = Array.from(e.dataTransfer.files).filter(f => {
                  if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB`); return false; }
                  return true;
                });
                setFiles(prev => [...prev, ...dropped]);
              }}
            >
              <Upload size={20} style={{ color: 'var(--color-text-muted)', marginBottom: 4 }} />
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>Click or drag files here</p>
              <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                Max 10MB per file. PNG, JPG, PDF, DOC, XLSX, CSV, TXT, LOG, ZIP
              </p>
            </div>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple
              accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt,.log,.zip" style={{ display: 'none' }} />

            {files.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {files.map((f, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', background: 'var(--color-bg-subtle, #f9fafb)',
                    borderRadius: 6, fontSize: 13,
                  }}>
                    <Paperclip size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => removeFile(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-muted)' }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn" onClick={() => navigate('/tickets')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit ticket'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
